import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { handleAcceptAppointment, viewChat, sortAppointmentsForService } from '../appointment/appointmentFunctions.js';
import StaffAppointmentDetails from './StaffAppointmentDetails.js';
import ViewChatModal from './ViewChatModalPage.js';
import { joinUserRoom, markRead, getUserGroups, updateRoomNotificationCount, getRoomUnreadMsgCount } from '../matrix/matrixFuntions.js';
import { Navigate } from "react-router-dom";
import PmsUrlConfig from '../../urlConfig.js';
import { toast } from 'react-toastify';
import { authenticateMatrixClient } from '../../services/matrixAuthService.js';

// Import refactored components
import AppointmentHeader from './components/AppointmentHeader.js';
import StaffAppointmentTable from './components/StaffAppointmentTable.js';
import StaffAppointmentCard from './components/StaffAppointmentCard.js';
import { useStaffAppointmentData } from './hooks/useStaffAppointmentData.js';

export default function ViewStaffAppointments() {
    const dispatch = useDispatch();
    const apptDetails = useSelector(state => state.appointmentDetails);
    const loginDetails = useSelector(state => state.loginDetails);
    const chatDetails = useSelector(state => state.chatDetails);
    const practiceDetails = useSelector(state => state.practiceDetails);
    const matrixClient = chatDetails.matrixClient;
    const isSortAsc = apptDetails.sortAscending;
    const selectedService = apptDetails.selectedService;
    
    // Track Matrix authentication attempts to prevent rate limiting
    const matrixAuthAttempted = useRef(false);

    if (!practiceDetails)
        <Navigate replace to={`${PmsUrlConfig.LOGIN_URL}`} />

    const practice = practiceDetails.practice;
    if (!practice)
        <Navigate replace to={`${PmsUrlConfig.LOGIN_URL}`} />
    
    const practiceId = practice._id;
    const token = loginDetails?.jwtToken || "";
    const loggedInUser = loginDetails?.loggedInUser;
    
    // Debug logging for authentication token
    console.log('=== AUTHENTICATION DEBUG ===');
    console.log('loginDetails:', loginDetails);
    console.log('loginDetails.jwtToken:', loginDetails?.jwtToken);
    console.log('Final token value:', token);
    console.log('Token type:', typeof token);
    console.log('Token length:', token.length);
    console.log('=== END AUTHENTICATION DEBUG ===');
    
    // Trigger Matrix authentication on component mount for staff users (with better rate limiting)
    useEffect(() => {
        console.log('=== STAFF COMPONENT - MATRIX AUTH CHECK ===');
        console.log('loggedInUser:', !!loggedInUser);
        console.log('matrixClient:', !!matrixClient);
        console.log('matrixAuthAttempted.current:', matrixAuthAttempted.current);
        
        if (loggedInUser && !matrixClient && !matrixAuthAttempted.current) {
            console.log('=== STAFF COMPONENT - TRIGGERING MATRIX AUTH ===');
            console.log('Staff user available:', !!loggedInUser);
            console.log('Matrix client not available:', !matrixClient);
            console.log('User Type:', loggedInUser.user_type);
            
            // Mark that we've attempted authentication IMMEDIATELY to prevent race conditions
            matrixAuthAttempted.current = true;
            console.log('Set matrixAuthAttempted to true');
            
            authenticateMatrixClient(loggedInUser, dispatch)
                .then(() => {
                    console.log('✅ Staff Matrix authentication completed successfully');
                })
                .catch((error) => {
                    console.error('❌ Staff Matrix authentication failed:', error);
                    // Only reset for non-rate-limit errors
                    if (!error.message?.includes('Too Many Requests') && !error.message?.includes('M_LIMIT_EXCEEDED')) {
                        console.log('Resetting auth flag for retry in 30 seconds');
                        setTimeout(() => {
                            matrixAuthAttempted.current = false;
                        }, 30000);
                    } else {
                        console.log('Rate limiting error - NOT resetting auth flag');
                    }
                });
        } else {
            console.log('=== STAFF COMPONENT - MATRIX AUTH STATUS ===');
            console.log('Staff user available:', !!loggedInUser);
            console.log('Matrix client available:', !!matrixClient);
            console.log('Auth attempt already made:', matrixAuthAttempted.current);
            console.log('Already authenticated, no user, or auth attempted');
        }
    }, [loggedInUser, matrixClient, dispatch]);
    
    // Component state
    const [showApptDetails, setShowApptDetails] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [activeFilters, setActiveFilters] = useState(null);
    const [showAcceptingSpinner, setShowAcceptingSpinner] = useState(false);

    // Use the unified staff appointment data hook with active filters
    useStaffAppointmentData(selectedService, activeFilters);

    // Get data from Redux
    var serviceTypes = [];
    if (loginDetails != null) {
        serviceTypes = loginDetails.serviceTypes;
    }

    // Get appointments for audit logging
    const allAppointments = apptDetails?.appointments || [];
    console.log('🔍 === STAFF APPOINTMENTS AUDIT LOG ===');
    console.log('🔍 Total appointments:', allAppointments.length);
    
    // Log audit information for each appointment
    allAppointments.forEach((appointment, index) => {
        console.log(`🔍 Appointment ${index + 1}:`, {
            _id: appointment._id,
            // Check for appointment_audits field from backend
            appointment_audits: appointment.appointment_audits,
            audit_info: appointment.audit_info,
            created_by_user: appointment.created_by_user,
            updated_by_user: appointment.updated_by_user,
            create_date_time: appointment.create_date_time,
            update_date_time: appointment.update_date_time,
            // Check nested structures
            appointment_basic_details: appointment.appointment_basic_details,
            // Log the entire appointment structure for first few appointments
            ...(index < 3 && { full_appointment: appointment })
        });
    });
    
    var appointments = null;
    var roomsNotificationCount = apptDetails.roomsNotificationCount;
    if (apptDetails != null) {
        appointments = apptDetails.appointmentsForService;
        console.log('=== APPOINTMENTS DISPLAY DEBUG ===');
        console.log('Selected Service:', selectedService);
        console.log('Appointments from Redux:', appointments);
        console.log('Number of appointments:', appointments ? appointments.length : 0);
        console.log('=== END DISPLAY DEBUG ===');
        // selectedAppointment is managed by useState, not from Redux
    }

    // Filter handler function
    const handleFilterChange = (filter) => {
        console.log('=== FILTER CHANGE ===');
        console.log('New filter:', filter);
        console.log('Previous filter:', activeFilters);
        
        setActiveFilters(filter);
        
        // The hook will automatically handle the API call with the new filter
        // No need to manually call getAppointmentsByFilter here
        console.log('Filter state updated - hook will handle API refresh');
    };

    // Apply filters to appointments (only for client-side filtering when needed)
    const getFilteredAppointments = (appointmentsToFilter) => {
        if (!appointmentsToFilter || !activeFilters) {
            return appointmentsToFilter;
        }

        // If we have an active filter, it means the data is already filtered from the backend
        // Only apply client-side filtering for associate filter (since backend doesn't support it yet)
        if (activeFilters.type === 'associate') {
            return appointmentsToFilter.filter((appointment) => {
                const associateName = appointment.appointment_basic_details?.associate_name || '';
                return associateName.toLowerCase().includes(activeFilters.value.toLowerCase());
            });
        }

        // For date and status filters, the backend has already filtered the data, so return as-is
        return appointmentsToFilter;
    };

    // Get filtered appointments for display
    const filteredAppointments = getFilteredAppointments(appointments);
    
    console.log('=== FILTERED APPOINTMENTS DEBUG ===');
    console.log('Active filters:', activeFilters);
    console.log('Original appointments count:', appointments ? appointments.length : 0);
    console.log('Filtered appointments count:', filteredAppointments ? filteredAppointments.length : 0);
    console.log('=== END FILTERED DEBUG ===');


    const sortByDate = () => {
        dispatch({
            type: 'setSortAscending',
            payload: !isSortAsc
        });
        sortAppointmentsForService(selectedService, dispatch, token, "date", !isSortAsc)
    };

    const handleDeclineClick = (appt) => {
        dispatch({
            type: 'setSelectedAppointment',
            payload: appt
        });
        dispatch({
            type: 'setShowDeclineAppointmentModal',
            payload: true
        });
    }

    const handleViewDetails = (appt) => {
        setSelectedAppointment(appt)
        setShowApptDetails(true)
    }

    const handleChatClick = async (appt, loggedInUser, nearestPractice) => {
        try {
            console.log('=== HANDLE CHAT CLICK DEBUG ===');
            console.log('Appointment data:', appt);
            console.log('Appointment keys:', Object.keys(appt));
            console.log('created_by_user:', appt.created_by_user);
            
            setSelectedAppointment(appt);
            const selectedRoom = nearestPractice.synapse_room_id;
            
            if (selectedRoom) {
                console.log('=== CHAT CLICK - RESET UNREAD COUNT ===');
                console.log('Room ID:', selectedRoom);
                console.log('Current unread count before reset:', getRoomUnreadMsgCount(selectedRoom, roomsNotificationCount));
                
                // Step 1: Join user to room using improved joinUserRoom function
                const joinSuccess = await joinUserRoom(matrixClient, selectedRoom, loggedInUser.synapse_user_id, dispatch);
                
                // Step 2: Mark messages as read and reset unread count
                if (joinSuccess && matrixClient) {
                    console.log('Marking messages as read for room:', selectedRoom);
                    
                    // Mark messages as read in Matrix
                    markRead(loggedInUser, matrixClient, selectedRoom);
                    
                    // Reset unread count to 0 in Redux
                    updateRoomNotificationCount(selectedRoom, 0, dispatch);
                    
                    console.log('Unread count reset to 0 for room:', selectedRoom);
                }
                
                // Step 3: Show chat modal after successful join
                if (joinSuccess) {
                    await viewChat(appt, loggedInUser, nearestPractice, dispatch);
                } else {
                    // Join failed but was handled gracefully, still show chat
                    await viewChat(appt, loggedInUser, nearestPractice, dispatch);
                }
                
                console.log('=== END CHAT CLICK - RESET UNREAD COUNT ===');
                
            } else {
                // Check if appointment status is NEW before showing error toast
                const appointmentStatus = appt.nearest_practices?.[0]?.practice_appointment_status;
                if (appointmentStatus === 'NEW') {
                    // Show spinner for NEW appointments - actually accept the appointment to create chat room
                    console.log('Chat room not available for NEW appointment - accepting appointment to create room');
                    setShowAcceptingSpinner(true);
                    
                    try {
                        // Accept the appointment to create the chat room
                        const updatedAppt = await handleAcceptAppointment(
                            appt, 
                            loggedInUser, 
                            appt.created_by_user._id, 
                            appt.nearest_practices[0].service_id, 
                            appt.nearest_practices[0].location_name, 
                            practiceId, 
                            practice, 
                            dispatch, 
                            serviceTypes.find(st => st._id === appt.appointment_basic_details.service_type)?.service_type_name || 'Unknown', 
                            token
                        );
                        
                        // Wait for appointment to update with room info
                        setTimeout(() => {
                            setShowAcceptingSpinner(false);
                            // Use the updated appointment with synapse_room_id
                            const apptToUse = updatedAppt || appt;
                            // Update selectedAppointment with the new data
                            setSelectedAppointment(apptToUse);
                            // Try opening chat again after acceptance
                            handleChatClick(apptToUse, loggedInUser, apptToUse.nearest_practices?.[0]);
                        }, 1000);
                    } catch (acceptError) {
                        console.error('Failed to accept appointment:', acceptError);
                        setShowAcceptingSpinner(false);
                        toast.error("Failed to accept appointment. Please try again.");
                    }
                    return;
                } else {
                    // Show error toast for other statuses when selectedRoom is empty or null
                    toast.error("Error occurred. Unable to open chat");
                    return;
                }
            }
        } catch (error) {
            
            // Handle specific error cases gracefully
            if (error.response?.status === 401 || error.response?.status === 403) {
                dispatch({
                    type: 'setErrorMsg',
                    payload: 'No access'
                });
            } else if (error.message?.includes('not in room')) {
                dispatch({
                    type: 'setErrorMsg',
                    payload: 'No access'
                });
            } else {
                dispatch({
                    type: 'setErrorMsg',
                    payload: 'Error opening chat. Please try again.'
                });
            }
            
            setTimeout(() => {
                dispatch({ type: 'setErrorMsg', payload: null });
            }, 3000);
            
            // Don't proceed with chat if there's an error
            return;
        }
    }

    // Add verification function for debugging
    const verifyPracticeGroups = async () => {
        if (matrixClient && loggedInUser && practiceId) {
            console.log("🔍 === VERIFYING PRACTICE GROUP AND ROOM MEMBERSHIP ===");
            const membershipInfo = await getUserGroups(matrixClient, loggedInUser.synapse_user_id);
            
            // Check if user belongs to any practice-related groups
            const practiceGroups = membershipInfo.groups.filter(group => 
                group.name.toLowerCase().includes('practice') ||
                group.name.toLowerCase().includes('group') ||
                group.roomId.includes(practiceId)
            );
            
            // Check for practice-related rooms
            const practiceRooms = membershipInfo.rooms.filter(room => 
                room.name.toLowerCase().includes('practice') ||
                room.name.toLowerCase().includes('group') ||
                room.roomId.includes(practiceId)
            );
            
            const verification = {
                userGroups: membershipInfo.groups,
                userRooms: membershipInfo.rooms,
                practiceRelatedRooms: practiceRooms,
                practiceGroups: practiceGroups,
                hasPracticeMembership: practiceGroups.length > 0,
                totalJoined: membershipInfo.totalJoined
            };
            
            console.log("📊 Membership Verification Results:", verification);
            return verification;
        } else {
            console.log("❌ Cannot verify - missing matrixClient, loggedInUser, or practiceId");
            return null;
        }
    };

    // Auto-verify on component mount
    useState(() => {
        setTimeout(() => {
            verifyPracticeGroups();
        }, 2000); // Wait 2 seconds for matrix client to initialize
    });

    return (
        <div className="h-full w-full bg-gray-100 px-4">
            <div className="h-full w-full">
                <AppointmentHeader onFilterChange={handleFilterChange} />
                
                <div className='bg-white rounded-xl shadow-lg overflow-hidden mx-2 lg:mx-4 mb-6'>
                    <div className='min-h-[400px]'>
                        {/* Desktop Table View */}
                        <StaffAppointmentTable 
                            appointments={filteredAppointments}
                            serviceTypes={serviceTypes}
                            isSortAsc={isSortAsc}
                            onSortByDate={sortByDate}
                            onAcceptAppointment={handleAcceptAppointment}
                            onDeclineAppointment={handleDeclineClick}
                            onViewDetails={handleViewDetails}
                            onChat={handleChatClick}
                            onEditAppointment={handleViewDetails}
                            roomsNotificationCount={roomsNotificationCount}
                            loggedInUser={loggedInUser}
                            matrixClient={matrixClient}
                            onMarkRead={markRead}
                            practiceId={practiceId}
                            dispatch={dispatch}
                            token={token}
                            practiceDetails={practiceDetails}  //  Add practiceDetails prop
                        />
                        
                        {/* Mobile Card Layout */}
                        {filteredAppointments != null && filteredAppointments.map((svcMsg, index) => (
                            <StaffAppointmentCard
                                key={index}
                                svcMsg={svcMsg}
                                serviceTypes={serviceTypes}
                                onAcceptAppointment={handleAcceptAppointment}
                                onDeclineAppointment={handleDeclineClick}
                                onViewDetails={handleViewDetails}
                                onChat={handleChatClick}
                                loggedInUser={loggedInUser}
                            />
                        ))}
                    </div>
                </div>

                {/* Modals */}
                {showApptDetails && selectedAppointment && (
                    <StaffAppointmentDetails
                        selectedAppointment={selectedAppointment}
                        showApptDetails={showApptDetails}
                        handleApptDetailsClose={() => setShowApptDetails(false)}
                        loggedInUser={loggedInUser}
                        dispatch={dispatch}
                        serviceTypes={serviceTypes}
                        practiceId={practiceId}
                        token={token}
                    />
                )}

                {apptDetails.showDeclineAppointmentModal && selectedAppointment && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                            <h3 className="text-lg font-semibold mb-4">Decline Appointment</h3>
                            <p className="text-gray-600 mb-6">Are you sure you want to decline this appointment?</p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        dispatch({
                                            type: 'setShowDeclineAppointmentModal',
                                            payload: false
                                        });
                                    }}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        // Handle decline logic here
                                        dispatch({
                                            type: 'setShowDeclineAppointmentModal',
                                            payload: false
                                        });
                                    }}
                                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                                >
                                    Decline
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <ViewChatModal 
                selectedAppointment={selectedAppointment}
                serviceTypes={serviceTypes}
                />

                {/* Accepting Appointment Spinner Modal */}
                {showAcceptingSpinner && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 flex flex-col items-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                            <p className="text-gray-700 text-center font-medium">Accepting appointment</p>
                            <p className="text-gray-500 text-sm text-center mt-2">Please wait while the appointment is being processed...</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
